/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/SeitenRouter.tsx
 *
 * Der Router einer Seite, die auf dem Server entsteht (Next.js-Phase 2,
 * docs/NEXTJS_MIGRATION.md, Erkenntnisse 15–19).
 *
 * Die Server-Seite rendert dieselbe Komponente wie die Single-Page-App — die
 * braucht einen React Router, weil sie `Link`, `useNavigate`, `useLocation`
 * und `useSearchParams` benutzt. Ein `BrowserRouter` geht nicht: Auf dem
 * Server gibt es kein `window`, und im Browser wuerde ein Klick auf einen
 * anderen Pfad nur die Komponente tauschen, waehrend die Seite die
 * Server-Seite bleibt (falscher Titel, falsche Metadaten, keine App-Huelle).
 *
 * Die Regel:
 * - Ein ANDERER Pfad ist ein echter Seitenaufruf (`location.assign`/`replace`)
 *   — das Ziel liefert der Server: wieder eine Server-Seite oder die App.
 * - Bleibt der Pfad gleich und aendern sich nur Query oder Anker (Zahlweise,
 *   Waehrung, Filter), nimmt der Router die History-API und rendert neu, ohne
 *   die Seite neu zu laden. Zurueck/Vor zwischen solchen Staenden kommt ueber
 *   `popstate` zurueck.
 *
 * `start` ist der Ort, den der Server gerendert hat — derselbe Wert auf beiden
 * Seiten, sonst verwirft React das Server-HTML.
 *
 * Entstanden in paperball.finance und 121surveys (04.10.2026); dort stand er
 * zweimal wortgleich in `src/OeffentlicheSeite.tsx`.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Router, createPath, parsePath, type Navigator, type Path, type To } from 'react-router-dom'

export function SeitenRouter({ start, children }: { start: Path; children: ReactNode }) {
  const [ort, setOrt] = useState<Path>(start)

  // Zurueck/Vor zwischen zwei Staenden derselben Seite.
  useEffect(() => {
    const zurueck = () => {
      const { pathname, search, hash } = window.location
      setOrt({ pathname, search, hash })
    }
    window.addEventListener('popstate', zurueck)
    return () => window.removeEventListener('popstate', zurueck)
  }, [])

  const navigator = useMemo<Navigator>(() => {
    const href = (to: To) => (typeof to === 'string' ? to : createPath(to))
    const wechsel = (to: To, ersetzen: boolean) => {
      const ziel = typeof to === 'string' ? parsePath(to) : to
      const pfad = ziel.pathname ?? window.location.pathname
      if (pfad !== window.location.pathname) {
        if (ersetzen) window.location.replace(href(to))
        else window.location.assign(href(to))
        return
      }
      const neu: Path = { pathname: pfad, search: ziel.search ?? '', hash: ziel.hash ?? '' }
      if (ersetzen) window.history.replaceState(null, '', createPath(neu))
      else window.history.pushState(null, '', createPath(neu))
      setOrt(neu)
    }
    return {
      createHref: href,
      go: (schritte: number) => window.history.go(schritte),
      push: (to: To) => wechsel(to, false),
      replace: (to: To) => wechsel(to, true),
    }
  }, [])

  return (
    <Router location={ort} navigator={navigator}>
      {children}
    </Router>
  )
}
